import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

// FilterDropdown — a custom-styled select for table filter controls. The trigger is a rounded,
// bordered button (border matches the search input's `border-card-bd`) with a subtle shadow and
// a chevron; opening it reveals a soft-shadowed popover whose selected item is highlighted with
// the app accent (soft background + accent text). Items carry no icons, by design.
export default function FilterDropdown({ value, onChange, options, ariaLabel, minWidth = 150, height, menuMaxHeight, variant = "outline", prefix = null, disabled = false, fullWidth = false }) {
	const [open, setOpen] = useState(false);
	const ref = useRef(null);

	// Close on outside click / Escape while open.
	useEffect(() => {
		if (!open) return undefined;
		const onDocClick = (e) => {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false);
		};
		const onKey = (e) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onDocClick);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDocClick);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	const selected = options.find((o) => o.value === value) || options[0];

	// An option may carry a `subtitle` — a second, quieter line under the label (a
	// person's role under their name). Rendered in BOTH the trigger and the menu so the
	// selected value reads the same as the option that produced it.
	const renderLabel = (opt, { withPrefix = false } = {}) => {
		if (!opt) return null;
		if (!opt.subtitle) {
			return (
				<span className="truncate">
					{withPrefix && prefix && <span className="font-normal text-text-3">{prefix} </span>}
					{opt.label}
				</span>
			);
		}
		// `truncate` goes on each line, never the flex column: on the column it sets
		// white-space:nowrap and collapses the two lines back into one.
		return (
			<span className="flex flex-col min-w-0 text-left">
				<span className="truncate">
					{withPrefix && prefix && <span className="font-normal text-text-3">{prefix} </span>}
					{opt.label}
				</span>
				<span className="truncate text-xs font-normal leading-tight text-text-3">{opt.subtitle}</span>
			</span>
		);
	};

	return (
		<div
			className={fullWidth ? "relative flex min-w-0" : "relative inline-flex"}
			style={fullWidth ? { width: "100%" } : undefined}
			ref={ref}
		>
			<button
				type="button"
				aria-label={ariaLabel}
				aria-haspopup="listbox"
				aria-expanded={open}
				disabled={disabled}
				onClick={() => { if (!disabled) setOpen((v) => !v); }}
				// `width:100%` inline, not `w-full`: this theme overrides Tailwind's width scale
				// with a tiny custom one, so `w-full` is a no-op here.
				style={{ minWidth, ...(height ? { height } : {}), ...(fullWidth ? { width: "100%" } : {}) }}
				// `border-solid` + `box-border` are required because a global `button { all: unset }`
				// reset (Toggle.css / SwitchStyles.css) strips border-style and box-sizing from every
				// button; the `border` utility only sets width, so without `border-solid` the border
				// (which must match the search input's `border-card-bd`) renders invisibly.
				// `tertiary` — a borderless, shadowless header control (period / comparison): no
				// resting border or background, just text + chevron, with a soft hover fill.
				className={`${
					variant === "tertiary"
						? "inline-flex items-center justify-between gap-2 box-border rounded-ctrl border border-solid border-transparent bg-transparent px-3 py-1.5 text-[13px] font-medium text-text-2 transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
						: "inline-flex items-center justify-between gap-2 box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 py-1.5 text-[13px] font-medium text-text shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
				} ${disabled ? "cursor-not-allowed opacity-50" : `cursor-pointer ${variant === "tertiary" ? "hover:bg-surface-2 hover:text-text" : "hover:bg-surface-2"}`}`}
			>
				{renderLabel(selected, { withPrefix: true })}
				<FontAwesomeIcon
					icon={icon({ name: "chevron-down", style: "solid" })}
					className={`shrink-0 text-[11px] text-text-3 transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</button>

			{open && (
				<div
					role="listbox"
					style={menuMaxHeight ? { maxHeight: menuMaxHeight } : undefined}
					className="absolute left-0 top-full z-50 mt-1 max-h-[280px] min-w-full overflow-auto rounded-card border border-card-bd bg-card p-1 shadow-lg"
				>
					{options.map((o) => {
						const isSelected = o.value === value;
						const isDisabled = !!o.disabled;
						return (
							<button
								key={o.value === "" ? "__all__" : o.value}
								type="button"
								role="option"
								aria-selected={isSelected}
								aria-disabled={isDisabled || undefined}
								onClick={() => {
									if (isDisabled) return;
									onChange(o.value);
									setOpen(false);
								}}
								// width:100% inline (not `w-full`, a no-op under this theme's width scale) so the
								// selected/hover highlight fills the menu width minus the panel's padding;
								// `box-border`/`block` undo the global `button { all: unset }` reset.
								style={{ width: "100%" }}
								// `truncate` only when there is a single line: on a two-line option it sets
								// white-space:nowrap on the button and collapses the subtitle back onto the
								// label. renderLabel truncates each line itself.
								className={`block box-border ${o.subtitle ? "" : "truncate"} rounded-ctrl px-3 py-2 text-left text-[13px] transition-colors ${
									isDisabled
										? "cursor-not-allowed text-text-3 opacity-60"
										: isSelected
											? "cursor-pointer bg-primary-soft font-semibold text-primary-hover"
											: "cursor-pointer text-text-2 hover:bg-surface-2 hover:text-text"
								}`}
							>
								{renderLabel(o)}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}

FilterDropdown.propTypes = {
	// Numbers as well as strings: owner pickers key on user ids.
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func.isRequired,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			label: PropTypes.node,
			// Optional quieter second line under the label (e.g. a person's role).
			subtitle: PropTypes.node,
			// Optional: renders the option greyed out and non-interactive (e.g. a
			// "coming soon" grouping dimension). Omit for a normal, clickable option.
			disabled: PropTypes.bool,
		})
	).isRequired,
	ariaLabel: PropTypes.string,
	minWidth: PropTypes.number,
	// Optional fixed trigger height in px (e.g. 36 to line up with sibling controls).
	height: PropTypes.number,
	// Optional popover max-height in px (raise it so a long option list fits without a scrollbar).
	menuMaxHeight: PropTypes.number,
	variant: PropTypes.oneOf(["outline", "tertiary"]),
	prefix: PropTypes.string,
	disabled: PropTypes.bool,
	// Fill the container instead of sizing to content (form fields under a label).
	fullWidth: PropTypes.bool,
};
